import { useAuth } from '@/features/auth/useAuth';
import { apiFetch } from '@/lib/apiClient';
import type { ActivityPing, ActivityTarget, ActivityUiEvent } from '@coreltb/shared/validation';
import { type MutableRefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { activityTarget } from './activityTarget';

/**
 * Pomiar pod roczne podsumowanie (Dawid 2026-09-30): co minutę, gdy karta jest
 * widoczna, wysyłamy puls — na jakim ekranie / przy jakim rekordzie jest użytkownik
 * i czy w ogóle pracuje (ruch w ostatnich 5 min). Do tego kliknięcia w telefon /
 * mail i Ctrl+K. Serwer liczy każdą minutę raz, nawet przy kilku kartach.
 */
const PING_MS = 60_000;
const IDLE_MS = 5 * 60_000;
const MAX_OPENED = 30;
const MAX_EVENTS = 100;

interface Pending {
  opened: ActivityTarget[];
  events: Record<ActivityUiEvent, number>;
}

const emptyPending = (): Pending => ({ opened: [], events: { call: 0, mail: 0, search: 0 } });

function useInputTracking(
  lastInputAt: MutableRefObject<number>,
  pending: MutableRefObject<Pending>,
): void {
  useEffect(() => {
    const touch = () => {
      lastInputAt.current = Date.now();
    };
    const bump = (event: ActivityUiEvent) => {
      const { events } = pending.current;
      events[event] = Math.min(events[event] + 1, MAX_EVENTS);
    };
    const onClick = (e: MouseEvent) => {
      touch();
      const link =
        e.target instanceof Element
          ? e.target.closest('a[href^="tel:"], a[href^="mailto:"]')
          : null;
      if (link) bump(link.getAttribute('href')?.startsWith('tel:') ? 'call' : 'mail');
    };
    const onKey = (e: KeyboardEvent) => {
      touch();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') bump('search');
    };
    const opts = { capture: true, passive: true } as const;
    const passive: [string, () => void][] = [
      ['pointermove', touch],
      ['scroll', touch],
      ['touchstart', touch],
    ];
    window.addEventListener('click', onClick, opts);
    window.addEventListener('keydown', onKey, opts);
    for (const [name, fn] of passive) window.addEventListener(name, fn, opts);
    return () => {
      window.removeEventListener('click', onClick, opts);
      window.removeEventListener('keydown', onKey, opts);
      for (const [name, fn] of passive) window.removeEventListener(name, fn, opts);
    };
  }, [lastInputAt, pending]);
}

export function useActivityTracker(): void {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  const lastInputAt = useRef(Date.now());
  const pending = useRef<Pending>(emptyPending());
  const enabled = Boolean(user) && user?.actualRole !== 'klient';

  useInputTracking(lastInputAt, pending);

  useEffect(() => {
    pathRef.current = pathname;
    const { target } = activityTarget(pathname);
    if (target && pending.current.opened.length < MAX_OPENED) pending.current.opened.push(target);
  }, [pathname]);

  useEffect(() => {
    if (!enabled) return;
    let lastSentAt = 0;
    const send = (active: boolean) => {
      const { section, target } = activityTarget(pathRef.current);
      const body: ActivityPing = {
        active,
        section,
        target,
        ...pending.current,
        mobile: window.matchMedia('(pointer: coarse)').matches,
        dark: document.documentElement.classList.contains('dark'),
      };
      pending.current = emptyPending();
      lastSentAt = Date.now();
      apiFetch('/api/activity/ping', { method: 'POST', body, keepalive: true }).catch(() => {});
    };
    const tick = () => {
      if (document.visibilityState === 'visible') send(Date.now() - lastInputAt.current < IDLE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastInputAt.current = Date.now();
        if (Date.now() - lastSentAt >= PING_MS) tick();
      } else if (
        pending.current.opened.length > 0 ||
        Object.values(pending.current.events).some(Boolean)
      ) {
        send(false);
      }
    };

    // Pierwszy puls od razu — liczy się też szybkie zajrzenie i godzina startu dnia.
    tick();
    const interval = window.setInterval(tick, PING_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);
}
